/* eslint-disable no-undef */

import { useState, useEffect, useCallback, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, onAuthStateChanged, signOut, updateProfile } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, onSnapshot, collection} from 'firebase/firestore';
//import { useExamSessionGuard } from "./hooks/useExamSessionGuard"; 
const Header = () => {
  return (
    <div className="fixed top-0 left-0 w-full z-50 bg-[#2a1839] p-4 shadow-lg border-b border-gray-700 flex items-center">
      <a href="https://www.astroterapeutica.com" target="_blank" rel="noopener noreferrer">
      <img
        src="https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/00c2bf-f35-7edf-dd84-73a807de303_62d86e44-6c98-4f6f-8a9d-9f0538d277b5.png"
        alt="Logo de la aplicación"
        className="h-10 rounded-full"
      />
      </a>
    </div>
  );
};

const WarningModal = ({ onAccept }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md text-center">
        <div className="text-red-600 text-6xl mb-4">⚠️</div>
        <h2 className="text-2xl font-bold text-red-600 mb-4">¡ADVERTENCIA!</h2>
        <p className="text-lg text-gray-700 mb-6">
          Has salido de la pestaña durante el examen. Si vuelves a salir, tu sesión caducará automáticamente y perderás este intento.
        </p>
        <p className="text-base text-gray-600 mb-6 font-semibold">
          ¿Entiendes y aceptas continuar?
        </p>
        <button
          onClick={onAccept}
          className="w-full py-3 px-6 bg-red-600 text-white font-bold rounded-lg shadow-md hover:bg-red-700 transition-colors"
        >
          Acepto y Entiendo
        </button>
      </div>
    </div>
  );
};

const useExamSessionGuard = ({ db, user, examState, setView, firebaseConfig, setShowWarningModal }) => {
  const hasShownWarningRef = useRef(false);
  const abandonedOnceRef = useRef(false);
const warningAlreadyGivenRef = useRef(false);
  useEffect(() => {
    if (!user || !db) return;

    const handleBeforeUnload = (e) => {
      if (examState.isExamActive) {
        e.preventDefault();
        e.returnValue = "Si sales perderás el examen actual.";
      }
    };

    const handleVisibilityChange = async () => {
      if (document.hidden && examState.isExamActive) {
        // Usuario salió de la pestaña
        if (!abandonedOnceRef.current) {
          // Primera vez que sale - marcar como abandonado una vez
          abandonedOnceRef.current = true;
        } else {
          // Segunda vez que sale - terminar sesión
          await handleExamAbandon();
        }
      } else if (!document.hidden && examState.isExamActive && abandonedOnceRef.current && !hasShownWarningRef.current) {
        // Usuario volvió después de haber salido una vez
        if (!warningAlreadyGivenRef.current) {
          // Primera vez que se muestra la advertencia
          hasShownWarningRef.current = true;
          warningAlreadyGivenRef.current = true; // Marcar que ya se dio la advertencia globalmente
          setShowWarningModal(true);
        } else {
          // Ya se había dado la advertencia antes, terminar sesión directamente
          await handleExamAbandon();
        }
      }
    };

    const handleExamAbandon = async () => {
      const appId = 'examenes';
      const userDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/user_data/exam_status`);

      const docSnap = await getDoc(userDocRef);
      let attempts = docSnap.exists() ? (docSnap.data().attempts || 0) : 0;

      attempts += 1;

      await setDoc(userDocRef, { attempts }, { merge: true });

      setView("sessionExpired");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [db, user, examState.isExamActive, firebaseConfig, setView, setShowWarningModal]);

  // Reset de las referencias cuando se inicia un nuevo examen
   useEffect(() => {
    if (examState.isExamActive && examState.currentQuestionIndex === 0) {
      hasShownWarningRef.current = false;
      abandonedOnceRef.current = false;
      // NO resetear warningAlreadyGivenRef.current - debe persistir entre exámenes
    }
  }, [examState.isExamActive, examState.currentQuestionIndex]);
};

// Componente para manejar la autenticación
const AuthScreen = ({ onLogin, onRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isRegistering, setIsRegistering] = useState(true);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (isRegistering) {
      onRegister(name, email, password).catch(err => setError(err.message));
    } else {
      onLogin(email, password).catch(err => setError(err.message));
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-sm">
        <h2 className="text-3xl font-bold text-center mb-6 text-gray-800">{isRegistering ? 'Regístrate para contestar el examen' : 'Iniciar Sesión'}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {isRegistering && (
            <div>
              <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="name">Ingresa tu Nombre:</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors"
                required
              />
            </div>
          )}
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="email">Ingresa tu Email:</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors"
              required
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="password">Crea una Contraseña:</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors"
              required
            />
          </div>
          {error && <p className="text-red-500 text-sm text-center">{error}</p>}
          <button
            type="submit"
            className="w-full py-3 px-6 bg-blue-600 text-white font-bold rounded-lg shadow-md hover:bg-blue-700 transition-colors transform hover:scale-105"
          >
            {isRegistering ? 'Registrarse' : 'Iniciar Sesión'}
          </button>
        </form>
        <div className="text-center mt-4">
          <p className="text-sm text-gray-600">
            {isRegistering ? '¿Ya tienes una cuenta?' : '¿No tienes una cuenta?'}
            <button
              onClick={() => setIsRegistering(!isRegistering)}
              className="text-blue-600 font-semibold ml-1 hover:underline"
            >
              {isRegistering ? 'Inicia Sesión' : 'Regístrate'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

// Componente de la página de resultados (solo visible para el administrador)
const ResultsPage = ({ db, onSignOut }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

const getOffer = (score) => {
  if (score >= 0 && score <= 6) {
    return "Oferta A:\nFormación Online\n35% de descuento";
  } else if (score >= 7 && score <= 9) {
    return "Oferta B:\nPremium Avanzada\nDescuento especial";
  } else if (score === 10) {
    return "Oferta C:\nMedia Beca";
  }
  return "Sin oferta";
};
  
  useEffect(() => {
    // Escucha cambios en los resultados del examen en tiempo real
    const resultsCollectionRef = collection(db, `artifacts/examenes/public/data/exam_results`);
    const unsubscribe = onSnapshot(resultsCollectionRef, (querySnapshot) => {
      const resultsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const offer = getOffer(data.score); // Determine the offer for each user
        return { id: doc.id, ...data, offer };
      });
      setResults(resultsData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching results:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl text-gray-600">Cargando resultados...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-100 pt-28">
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-4xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800">Resultados del Examen</h2>
          <button onClick={onSignOut} className="py-2 px-4 bg-red-600 text-white rounded-lg shadow-md hover:bg-red-700 transition-colors">Cerrar Sesión</button>
        </div>
        {results.length > 0 ? (
          <table className="min-w-full bg-white rounded-lg overflow-hidden shadow-md">
            <thead>
              <tr className="bg-blue-600 text-white">
                <th className="py-3 px-4 text-left">Nombre de Usuario</th>
                <th className="py-3 px-4 text-left">Puntuación</th>
                <th className="py-3 px-4 text-left">Respuestas</th>
                 <th className="py-3 px-4 text-left">Oferta</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result) => (
                <tr key={result.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-4 text-gray-700">{result.userName}</td>
                  <td className="py-3 px-4 text-gray-700 font-semibold">{result.score} / 10</td>
                  <td className="py-3 px-4">
                    <ul className="list-disc list-inside text-sm text-gray-600">
                     
                      {result.answers.sort((a, b) => a.originalIndex - b.originalIndex).map((answer) => (
                        <li key={answer.originalIndex} className="mb-1">
                        Pregunta {answer.question}: <span className={`font-semibold ${answer.isCorrect ? "text-green-500" : "text-red-500"}`}>{answer.isCorrect ? "Correcta" : "Incorrecta"}</span>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="py-3 px-4 text-gray-700 font-semibold whitespace-pre-line">{result.offer}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-center text-gray-600 py-10">No hay resultados de examen aún.</p>
        )}
      </div>
    </div>
  );
};


const getRegionalPricing = async (user, db, offerType) => {
  try {
    // Solo aplicar para ofertaA
   /* if (offerType !== 'ofertaA') {
      return null;
    }*/

    const appId = 'examenes';
    
    // 1. Obtener datos del usuario (ubicación)
    const userDocRef = doc(db, `artifacts/${appId}/users/${user.uid}`);
    const userSnap = await getDoc(userDocRef);
    
    if (!userSnap.exists()) {
      console.warn('No se encontraron datos del usuario');
      return null;
    }
    
    const userData = userSnap.data();
    const countryCode = userData.countryCode;
    const continentCode = userData.continentCode;
    
    console.log('🌍 Usuario ubicación:', { countryCode, continentCode });
    
    // 2. Obtener configuración de ofertas regionales
    const offerDocRef = doc(db, `artifacts/${appId}/ofertas/${offerType}`);
    const offerSnap = await getDoc(offerDocRef);
    
    if (!offerSnap.exists() || !offerSnap.data().ofertaZonas) {
      console.warn('No se encontró configuración de ofertaZonas');
      return null;
    }
    
    const ofertaZonas = offerSnap.data().ofertaZonas;
    
    // 3. Lógica de selección regional
    let selectedZone = null;
    let zoneKey = '';
    
    // Prioridad 1: Si es Argentina, usar AR directamente
    if (countryCode === 'AR' && ofertaZonas.AR) {
      selectedZone = ofertaZonas.AR;
      zoneKey = 'AR';
      console.log('✅ Zona seleccionada: Argentina (AR)');
    }
    // Prioridad 2: Si no es AR, usar continentCode
    else if (continentCode && ofertaZonas[continentCode]) {
      selectedZone = ofertaZonas[continentCode];
      zoneKey = continentCode;
      console.log(`✅ Zona seleccionada: ${continentCode}`);
    }
    // Prioridad 3: Default a NA si no existe la zona
    else if (ofertaZonas.NA) {
      selectedZone = ofertaZonas.NA;
      zoneKey = 'NA';
      console.log('✅ Zona seleccionada: North America (Default)');
    }
    
    if (!selectedZone) {
      console.warn('❌ No se pudo determinar zona regional');
      return null;
    }
    
    return {
      zone: zoneKey,
      countryCode: countryCode,
      continentCode: continentCode,
      pricing: selectedZone // ✅ Directamente la zona, sin .normal
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo pricing regional:', error);
    return null;
  }
};


// Componente de la página de resultados para el usuario
const UserResultsPage = ({ onSignOut, user, db }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [showFireworks, setShowFireworks] = useState(false);
const [regionalPricing, setRegionalPricing] = useState(null);
  // Componente del gráfico de torta animado
  const AnimatedPieChart = ({ score, total = 10 }) => {
    const percentage = (score / total) * 100;
    const radius = 80;
    const strokeWidth = 12;
    const normalizedRadius = radius - strokeWidth * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDasharray = `${circumference} ${circumference}`;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    // Determinar colores basados en el score
    const getColors = (score) => {
      if (score >= 9) return { primary: '#10b981', secondary: '#d1fae5', text: '#059669' }; // Verde
      if (score >= 7) return { primary: '#3b82f6', secondary: '#dbeafe', text: '#1d4ed8' }; // Azul
      if (score >= 4) return { primary: '#f59e0b', secondary: '#fef3c7', text: '#d97706' }; // Amarillo
      return { primary: '#ef4444', secondary: '#fee2e2', text: '#dc2626' }; // Rojo
    };

    const colors = getColors(score);

    return (
      <div className="flex flex-col items-center">
        <div className="relative">
          <svg
            height={radius * 2}
            width={radius * 2}
            className="transform -rotate-90"
          >
            {/* Círculo de fondo */}
            <circle
              stroke={colors.secondary}
              fill="transparent"
              strokeWidth={strokeWidth}
              r={normalizedRadius}
              cx={radius}
              cy={radius}
            />
            {/* Círculo de progreso animado */}
            <circle
              stroke={colors.primary}
              fill="transparent"
              strokeWidth={strokeWidth}
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              r={normalizedRadius}
              cx={radius}
              cy={radius}
              className="transition-all duration-1000 ease-out"
              style={{
                animation: 'drawCircle 1.5s ease-out forwards',
              }}
            />
          </svg>
          
          {/* Texto central con score */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div 
                className="text-4xl font-bold transition-all duration-1000 delay-500"
                style={{ color: colors.text }}
              >
                {score}
              </div>
              <div className="text-sm text-gray-500 font-medium">de {total}</div>
            </div>
          </div>
        </div>
        
        {/* Porcentaje debajo del gráfico 
        <div className="mt-4 text-center">
          <div 
            className="text-2xl font-bold transition-all duration-1000 delay-1000"
            style={{ color: colors.text }}
          >
            {percentage.toFixed(0)}%
          </div>
          <div className="text-sm text-gray-600">Puntuación obtenida</div>
        </div>*/}
      </div>
    );
  };




const FinalImageSectionav = () => {
  return (
    <div className="mt-12 mb-8">
      <div className="w-full max-w-6xl mx-auto px-4">
        {/* Imagen para desktop - oculta en mobile */}
        <img
          src="https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/ad5364-3f-701e-2ab8-46187fadb5a_a5bf8806-569c-4fa6-a948-11aaadc0be60.webp"
          alt="Formación Online 2025"
          className="hidden md:block w-full h-auto rounded-2xl shadow-lg object-cover"
          loading="lazy"
        />
        
        {/* Imagen para mobile - oculta en desktop */}
        <img
          src="https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/8c22a0-7728-ef7f-4b4-fa1b31723fe_7d428dac-6fac-4049-b023-811d4db2e365.webp"
          alt="Formación Online 2025"
          className="block md:hidden w-full h-auto rounded-2xl shadow-lg object-cover"
          loading="lazy"
        />
      </div>
    </div>
  );
};

const TestimonialSectionav = () => {
  return (
    <div 
      className=" py-10 px-6"
      style={{
        fontFamily: "'DM Sans', sans-serif"
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
          .mb-8{
          margin-bottom:1rem;
        }
      
      `}
      </style>

      <div className="max-w-4xl mx-auto text-center">
        

       

        {/* Información sobre webinars */}
        <div>
          <p 
            className="text-lg "
            style={{ color: '#4A2472' }}
          >
           En este Nivel Avanzado, para el cual es indispensable que ya sepas leer Cartas Natales, obtendrás herramientas extremadamente valiosas para llevar la<strong> interpretación a un nivel superior</strong>, tanto en adultos como en niños, así como para <strong>asesorar a parejas o a personas en relaciones </strong>de cualquier índole.
          </p>
        </div>
      </div>
    </div>
  );
};

const FinalImageSection = () => {
  return (
    <div className="mt-12 mb-8">
      <div className="w-full max-w-6xl mx-auto px-4">
        {/* Imagen para desktop - oculta en mobile */}
        <img
          src="https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/db5cf0f-5b0b-aa20-bac-b8ff8a6b4452_FORMACION-ONLINE-2025---SLIDER-LANDING-DESKTOP.webp"
          alt="Formación Online 2025"
          className="hidden md:block w-full h-auto rounded-2xl shadow-lg object-cover"
          loading="lazy"
        />
        
        {/* Imagen para mobile - oculta en desktop */}
        <img
          src="https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/e8bce4-dbf2-c71e-107b-fe50ad7d88c3_FORMACION-ONLINE-2025---SLIDER-LANDING-MOBILE.webp"
          alt="Formación Online 2025"
          className="block md:hidden w-full h-auto rounded-2xl shadow-lg object-cover"
          loading="lazy"
        />
      </div>
    </div>
  );
};

const TestimonialSectionofer = () => {
  return (
    <div 
      className="mt-12 py-16 px-6"
      style={{
        fontFamily: "'DM Sans', sans-serif"
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
        `}
      </style>

      <div className="max-w-4xl mx-auto text-center">
        {/* Cita principal en cursiva */}
    

        {/* Información sobre duración */}
        <div className="mb-6">
          <p 
            className="text-3xl md:text-3xl  mb-3"
            style={{ color: '#3094af' }}
          >
            Comienza a aprender HOY 
con la Formación Online más completa de Astrología
          </p>
        </div>

        {/* Información sobre webinars */}
        <div>
          <p 
            className="text-lg "
            style={{ color: '#4A2472' }}
          >
            Estudia a tu propio ritmo, cuando quieras y desde donde quieras.
          </p>
        </div>
      </div>
    </div>
  );
};



const TestimonialSection = () => {
  return (
    <div 
      className="mt-12 py-16 px-6"
      style={{
        fontFamily: "'DM Sans', sans-serif"
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
        `}
      </style>

      <div className="max-w-4xl mx-auto text-center">
        {/* Cita principal en cursiva */}
        <div className="mb-6">
          <p 
            className="text-2xl md:text-2xl italic leading-relaxed"
            style={{ color: '#3094af' }}
          >
            "Estuve años diseñando esta Formación, extremadamente completa, para que tengas la mejor calidad y experiencia de aprendizaje…
          </p>
          <p 
            className="text-2xl md:text-2xl italic leading-relaxed mt-2"
            style={{ color: '#3094af' }}
          >
            ¡Es mi deseo que aprendas a leer Cartas Natales!"
          </p>
        </div>

        {/* Nombre del autor */}
        <div className="mb-8">
          <p 
            className="text-xl font-bold"
            style={{ color: '#4A2472' }}
          >
            Pablo Flores Laymuns
          </p>
        </div>

        {/* Tres puntos separadores */}
        <div className="mb-8">
          <p 
            className="text-3xl font-bold"
            style={{ color: '#3094af' }}
          >
            ...
          </p>
        </div>

        {/* Información sobre duración */}
        <div className="mb-6">
          <p 
            className="text-3xl md:text-3xl  mb-3"
            style={{ color: '#3094af' }}
          >
            La <strong>Formación</strong> está pensada para completarse en <strong>12 meses</strong>
          </p>
        </div>

        {/* Información sobre webinars */}
        <div>
          <p 
            className="text-lg "
            style={{ color: '#4A2472' }}
          >
            Además <strong>desde el primer día y por 2 años</strong>, tendrás acceso a <strong>3 webinars mensuales en vivo con Pablo Flores.</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

const renderFormattedText = (text) => {
    if (!text) return null;
    
    // Reemplazar **texto** con <strong>texto</strong>
    const formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    return (
      <span dangerouslySetInnerHTML={{ __html: formattedText }} />
    );
  };




const BenefitsSection = () => {
  const renderFormattedText = (text) => {
    const formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    return <span dangerouslySetInnerHTML={{ __html: formattedText }} />;
  };

  const leftColumnBenefits = [
    "**✴︎ Comprender el propósito de la encarnación en esta vida e identificar el camino evolutivo del alma.**",
    "**✴︎ Entender por qué se repiten situaciones difíciles.**",
    "**✴︎ Transformar la realidad y avanzar con mayor conciencia.**",
    "**✴︎ Reconocer patrones heredados del sistema familiar.**"
  ];

  const rightColumnBenefits = [
    "**✴︎ Conectar con dones y potenciales únicos.**",
    "**✴︎ Comprender aprendizajes en el amor y los vínculos.**",
    "**✴︎ Descubrir por qué se atraen ciertas relaciones o parejas.**",
    "**✴︎ Orientar la vocación y la realización profesional.**",
    "**✴︎ Liberar bloqueos emocionales, miedos y condicionamientos.**"
  ];

  return (
    <div 
      className="mt-12 py-16 px-6 rounded-2xl"
      style={{
        backgroundImage: "url('https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/3e34463-e30-7acf-cd25-0d7e4b0c83_FORMACION-ONLINE-2025---SLIDER-LANDING-FONDO-DESKTOP.webp')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        fontFamily: "'DM Sans', sans-serif"
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
          
          .benefits-column {
            background-color: rgba(38, 20, 65, 0.2);
            backdrop-filter: blur(5px);
            transition: all 0.3s ease-in-out;
          }
          
          .benefits-column:hover {
            background-color: rgba(38, 20, 65, 0.25);
            transform: translateY(-4px);
          }
        `}
      </style>

      {/* Header */}
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-3xl font-bold mb-3 text-white ">
          {renderFormattedText("**En esta Formación podrás:**")}
        </h2>
      </div>

      {/* Dos columnas de beneficios */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
        {/* Columna Izquierda */}
        <div className="benefits-column rounded-2xl p-8">
          <ul className="space-y-6">
            {leftColumnBenefits.map((benefit, index) => (
              <li key={index} className="text-white text-lg leading-relaxed">
                {renderFormattedText(benefit)}
              </li>
            ))}
          </ul>
        </div>

        {/* Columna Derecha */}
        <div className="benefits-column rounded-2xl p-8">
          <ul className="space-y-6">
            {rightColumnBenefits.map((benefit, index) => (
              <li key={index} className="text-white text-lg leading-relaxed">
                {renderFormattedText(benefit)}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const AprenderasAv = () => {
  const bloques = [
    {
      id: 1,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/e6d4220-2455-25c3-200-ecf63d704eb6_icono-2.webp",
      title: "TRÁNSITOS Y PROGRESIONES",
      description: "Conocerás cómo interpretar los Tránsitos de Júpiter, Saturno, Urano, Neptuno, Plutón y Quirón. Con guías y consejos terapéuticos para ayudar a tus consultantes."
    },
    {
      id: 2,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/d37143-6b5e-571-0352-8dbf80c2_icono-1.webp",
      title: "PROGRESIONES SECUNDARIAS",
      description: "Comprenderás el significado de las Progresiones Secundarias del Sol y la Luna, una de las técnicas predictivas más precisas que existen, entendiendo cómo aplicarlas junto a la interpretación de los Tránsitos."
    },
    {
      id: 3,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/6362cef-1bfe-b221-5ca-60711415e577_icono-4.webp",
      title: "CICLOS Y SEPTENIOS",
      description: "Podrás leer los ciclos de tiempo, desafíos y aprendizajes de las distintas edades y etapas de la vida en la Tierra. Tanto para adultos, como para niños."
    },
    {
      id: 4,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/328fc-c7b-5b72-00f4-bfcb326e20_icono-3.webp",
      title: "REVOLUCIÓN SOLAR",
      description: "Aprenderás en detalle el arte de la interpretación de la Carta de la Revolución Solar, entendiendo cómo guiar y orientar a tus consultantes para que puedan sacar un mayor provecho a su año."
    },
    {
      id: 5,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/faa5bbc-2f08-ef57-7b5-51ad265451d2_icono-5.webp",
      title: "SINASTRÍA",
      description: "Comprenderás las dinámicas vinculares a partir de la interpretación y comparación de dos cartas."
    },
    {
      id: 6,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/8feb085-c44e-83d-c53-04a1e540f36d_icono-6.webp",
      title: "CARTAS COMPUESTAS",
      description: "Aprenderás a interpretar la carta de una relación. Entenderás las experiencias, aprendizajes, desafíos y el destino que se vive en conjunto. Obtendrás claves para aportar mayor fluidez a las relaciones."
    }
  ];

  return (
    <div 
      className="py-16 px-6"
      style={{
        backgroundColor: '#4A2472',
        fontFamily: "'DM Sans', sans-serif",
        borderRadius:'20px'
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
          
          .aprenderas-card {
            background-color: white;
            border-radius: 1rem;
            padding: 2rem;
            text-align: center;
          }
            .text-center{
            width:100%;
            }
        `}
      </style>

      <div className="text-center mb-12">
        <h2 
          className="text-3xl md:text-3xl font-bold mb-3"
          style={{ color: '#FBFAF3' }}
        >
          ¿Qué aprenderás en la Formación Premium Avanzada?
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {bloques.map((block) => (
          <div
            key={block.id}
            className="aprenderas-card"
          >
            {/* Imagen */}
            <div className="mb-4 flex justify-center">
              <img src={block.image} alt={block.title} className="w-24 h-24 object-contain" />
            </div>

            {/* Título */}
            <h3 
              className="text-xl md:text-xl font-bold mb-1"
              style={{ color: '#BF965E' }}
            >
              {block.title}
            </h3>

            {/* Descripción */}
            <p className="text-gray-800 text-base leading-relaxed">
              {block.description}
            </p>
          </div>
        ))}
      </div>
          <div className="text-center-botom">
        <a
          href="https://astroterapeutica.com/formacion-premium-avanzada-2025-descarga-programa"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: '15px',
               width: '60%',
    marginRight: '20%',
    marginLeft: '20%',
    marginTop: '2rem',
            fontWeight: 'bold',
            padding: '20px',
            backgroundColor: '#2A1839',
            color: 'white',
            borderRadius: '50px',
            border: '0',
            display: 'inline-block',
            textDecoration: 'none',
            textAlign: 'center',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#1e1129';
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#2A1839';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          DESCARGAR PROGRAMA Y CALENDARIO
        </a>
      </div>
    </div>
  );
};


 const DesadorroAv = () => {
  const estructuraBlocks = [
    {
      id: 1,
      icon: <i className="fa fa-arrow-circle-right" aria-hidden="true" style={{ fontSize: '25px', color: '#BF965E' }}></i>,
      title: "Clases Teóricas y Prácticas Septiembre 2025 – Noviembre 2026",
      description: "La Formación dura 13 meses, con clases en vivo con Pablo Flores y Pilar Saavedra, para integrar teoría y práctica desde el inicio."
    },
    {
      id: 2,
      icon: <i className="fa fa-arrow-circle-right" aria-hidden="true" style={{ fontSize: '25px', color: '#BF965E' }}></i>,
      title: "Seguimos acompañándote hasta Noviembre 2027",
      description: "Con webinars mensuales con Pablo Flores, para responder tus dudas y seguir practicando lecturas."
    },
    {
      id: 3,
      icon: <i className="fa fa-arrow-circle-right" aria-hidden="true" style={{ fontSize: '25px', color: '#BF965E' }}></i>,
      title: "Mucha práctica para que realmente aprendas",
      description: "No solo tendrás la mejor teoría, también verás muchas interpretaciones prácticas desde el inicio, con Pablo analizando cartas de alumnos en vivo, tema por tema."
    }
  ];

  return (
    <div 
      className="py-16 px-6"
      style={{
        backgroundColor: '#4A2472',
        fontFamily: "'DM Sans', sans-serif",
        borderRadius:'20px'
            }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
          
          .estructura-card {
            background-color: white;
            border-radius: 1rem;
            padding: 2rem;
            text-align: center;
          }
        `}
      </style>

      <div className="text-center mb-12">
        <h2 
          className="text-3xl md:text-3xl font-bold mb-3"
          style={{ color: '#FBFAF3' }}
        >
          Cómo se desarrolla el curso
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {estructuraBlocks.map((block) => (
          <div
            key={block.id}
            className="estructura-card"
          >
            {/* Ícono */}
            <div className="mb-4 flex justify-center">
              {block.icon}
            </div>

            {/* Título */}
            <h3 
              className="text-xl md:text-2xl font-bold mb-1"
              style={{ color: '#4A2472' }}
            >
              {block.title}
            </h3>

            {/* Descripción */}
            <p className="text-gray-800 text-base leading-relaxed">
              {block.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};


const MetodoAv = () => {
  const metodoBlocks = [
    {
      id: 1,
      icon: <i className="fa fa-arrow-circle-right" aria-hidden="true" style={{ fontSize: '25px', color: '#4a2472' }}></i>,
      title: "Miércoles y sábados durante 13 meses",
      description: "Tendrás 128 hs. de clases prácticas y teóricas durante 13 meses."
    },
    {
      id: 2,
      icon: <i className="fa fa-arrow-circle-right" aria-hidden="true" style={{ fontSize: '25px', color: '#4a2472' }}></i>,
      title: "Práctica entre alumnos",
      description: "Además, tendrás espacios especiales de prácticas supervisadas y así podrás ganar experiencia en la interpretación."
    },
    {
      id: 3,
      icon: <i className="fa fa-arrow-circle-right" aria-hidden="true" style={{ fontSize: '25px', color: '#4a2472' }}></i>,
      title: "Todas tus dudas son resueltas",
      description: "Ya sea en clase, en los webinars o siempre puedes mandar un mail con tus consultas que serán respondidas a la brevedad."
    },
    {
      id: 4,
      icon: <i className="fa fa-arrow-circle-right" aria-hidden="true" style={{ fontSize: '25px', color: '#4a2472' }}></i>,
      title: "+ 600 páginas de apuntes",
      description: "Exclusivos y detallados con toda la información que necesitas, escritos por Pablo Flores y Pilar Saavedra."
    },
    {
      id: 5,
      icon: <i className="fa fa-arrow-circle-right" aria-hidden="true" style={{ fontSize: '25px', color: '#4a2472' }}></i>,
      title: "Programa y calendario de estudio",
      description: "Para que puedas organizar los contenidos."
    },
    {
      id: 6,
      icon: <i className="fa fa-arrow-circle-right" aria-hidden="true" style={{ fontSize: '25px', color: '#4a2472' }}></i>,
      title: "Trabajo final de interpretación",
      description: "Con posibilidad de entrega mientras dure tu acceso a la Formación. Recibirás la certificación una vez que hayas aprobado el trabajo."
    },
    {
      id: 7,
      icon: <i className="fa fa-arrow-circle-right" aria-hidden="true" style={{ fontSize: '25px', color: '#4a2472' }}></i>,
      title: "Acceso ilimitado hasta Noviembre del 2028",
      description: "Para que puedes volver a ver los videos y clases, tanto teóricos como prácticos."
    }
  ];

  return (
    <div 
      className="py-16 px-6 fondo"
      style={{
        fontFamily: "'DM Sans', sans-serif"
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
          
          .metodo-card {
            background-color: white;
            border-radius: 1rem;
            border: 2px solid #BF965E;
            padding: 2rem;
            text-align: center;
          }

          .max-w-4xl {
            max-width: none;
          }

          .fondo {
            background: #FBFAF3;
            border-radius: 20px;
          }

          /* Estilos para el grid */
          .grid-container {
            display: grid;
            gap: 2rem;
          
            margin-left: auto;
            margin-right: auto;
          }
          
          @media (min-width: 1024px) {
            .grid-container {
              grid-template-columns: repeat(3, minmax(0, 1fr));
            }
            .grid-container .metodo-card:nth-child(7) {
              grid-column: 2 / 3;
            }
          }
        `}
      </style>

      <div className="text-center mb-12">
        <h2 
          className="text-3xl md:text-3xl  mb-3"
          style={{ color: '#bf965e' }}
        >
          <strong>Metodología de aprendizaje: </strong>
          <p>online, vivencial, grupal y en vivo</p>
        </h2>
      </div>

      <div className="grid-container">
        {metodoBlocks.map((block) => (
          <div
            key={block.id}
            className="metodo-card"
          >
            {/* Ícono */}
            <div className="mb-4 flex justify-center">
              {block.icon}
            </div>

            {/* Título y Subtítulo */}
            <h3 
              className="text-xl md:text-2xl font-bold mb-1"
              style={{ color: '#4A2472' }}
            >
              {block.title}
            </h3>
            
            {/* Descripción */}
            <p className="text-gray-800 text-base leading-relaxed">
              {block.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};


const ContenidosAv = () => {
  const contenidosBlocks = [
    {
      id: 1,
      icon: <i className="fa fa-arrow-circle-right" aria-hidden="true" style={{ fontSize: '25px', color: '#4a2472' }}></i>,
      title: "ASTROLOGÍA PREDICTIVA",
      subtitle: "Tránsitos, Progresiones, Ciclos y Revolución Solar",
      description: "Aprende técnicas predictivas modernas, desmitificándolas para dejar de verlas como castigos o situaciones a temer, entendiéndolas como las verdaderas oportunidades que son para potenciar nuestra evolución personal."
    },
    {
      id: 2,
      icon: <i className="fa fa-arrow-circle-right" aria-hidden="true" style={{ fontSize: '25px', color: '#4a2472' }}></i>,
      title: "DINÁMICAS VINCULARES",
      subtitle: "Sinastrías y Cartas Compuestas",
      description: "Integra las técnicas clave para interpretar las dinámicas vinculares y relaciones, para guiar y orientar a parejas, familias, socios, compañeros de trabajo… Ayudando y aconsejando con los posibles conflictos, desafíos y potencialidades que las relaciones tienen."
    }
  ];

  return (
    <div 
      className="py-16 px-6 fondo"
      style={{
        fontFamily: "'DM Sans', sans-serif"
      }}
    >
      
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
          
          .contenido-card {
            background-color: white;
            border-radius: 1rem;
            border: 2px solid #BF965E;
            padding: 2rem;
            text-align: center;
          }
        .max-w-4xl {
    max-width: none;
         }
    .fondo{
    background: #FBFAF3;
    border-radius: 20px;
    }
        `}
      </style>
 <div className="text-center mb-12">
        <h2 
          className="text-3xl md:text-3xl font-bold mb-3"
          style={{ color: '#4A2472' }}
        >
         Contenidos
        </h2>
        
      </div>


      {/* Grid de contenido - 2 bloques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {contenidosBlocks.map((block) => (
          <div
            key={block.id}
            className="contenido-card"
          >
            {/* Ícono */}
            <div className="mb-4 flex justify-center">
              {block.icon}
            </div>

            {/* Título y Subtítulo */}
            <h3 
              className="text-xl md:text-2xl font-bold mb-1"
              style={{ color: '#4A2472' }}
            >
              {block.title}
            </h3>
            <p className="text-sm italic mb-4" style={{ color: '#4A2472' }}>
              {block.subtitle}
            </p>

            {/* Descripción */}
            <p className="text-gray-800 text-base leading-relaxed">
              {block.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const SupportSection = () => {
  const renderFormattedText = (text) => {
    const formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    return <span dangerouslySetInnerHTML={{ __html: formattedText }} />;
  };

  const supportBlocks = [
    {
      id: 1,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/7acfb3f-4738-d84-e72-65b725da70d1_icono-1.webp",
      title: "**WEBINARS EN VIVO CADA MES CON PABLO FLORES**",
      description: "Participa en sesiones en vivo donde podrás resolver tus dudas, ver prácticas de interpretación y conectar con otros estudiantes."
    },
    {
      id: 2,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/ebb043-260d-4f78-bb46-40132577163_icono-4.webp",
      title: "**RESOLUCIÓN DE DUDAS PERSONALIZADA**",
      description: "Puedes enviar tus preguntas por mail y siempre recibirás una respuesta clara y oportuna."
    },
    {
      id: 3,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/34c0ce1-142-25ef-dcf-78b47a8fefc2_iconos-1.webp",
      title: "**PRÁCTICAS REALES CON OTROS ESTUDIANTES**",
      description: "Tendrás jornadas prácticas de interpretación guiadas por tutores, donde podrás aplicar lo aprendido leyendo Cartas Natales reales."
    }
  ];

  return (
    <div 
      className="mt-12 py-16 px-6 rounded-2xl"
      style={{
        backgroundColor: '#F5FBFF',
        fontFamily: "'DM Sans', sans-serif"
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
          
          .support-card {
            transition: all 0.3s ease-in-out;
          }
          
          .support-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          }
          
          .support-icon {
            transition: transform 0.3s ease-in-out;
          }
          
          .support-card:hover .support-icon {
            transform: scale(1.1);
          }
        `}
      </style>

      {/* Header */}
      <div className="text-center mb-12">
        <h2 
          className="text-3xl md:text-3xl font-bold mb-3"
          style={{ color: '#3094af' }}
        >
          {renderFormattedText("**Te acompañamos paso a paso en tu aprendizaje**")}
        </h2>
        <p 
          className="text-xl leading-relaxed"
          style={{ color: '#4A2472' }}
        >
          {renderFormattedText("En esta Formación no estarás solo: estarás acompañado por **Pablo Flores** y un **equipo de tutores expertos**.")}
        </p>
      </div>

      {/* Grid de soporte - 3 bloques */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {supportBlocks.map((block) => (
          <div
            key={block.id}
            className="support-card bg-white rounded-2xl p-8 text-center shadow-lg"
          >
            {/* Imagen/Ícono */}
            <div className="support-icon mb-6 flex justify-center">
              <img
                src={block.image}
                alt={`Ícono ${block.id}`}
                className="w-16 h-16 object-contain"
              />
            </div>

            {/* Título */}
            <h3 
              className="text-lg font-bold mb-4"
              style={{ color: '#3094af' }}
            >
              {renderFormattedText(block.title)}
            </h3>

            {/* Descripción */}
            <p className="text-gray-800 text-base leading-relaxed">
              {block.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};


const CertificationSection = () => {
  const renderFormattedText = (text) => {
    const formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    return <span dangerouslySetInnerHTML={{ __html: formattedText }} />;
  };

  const certificationBlocks = [
    {
      id: 1,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/f88c5f8-47a5-375-8f5-1b0f07cd82_icono-2.webp",
      title: "**VISIÓN TERAPÉUTICA Y EVOLUTIVA**",
      description: "Comprenderás la **estructura emocional, psicológica y espiritual** de una persona a través de su Carta Natal, guiando procesos de autoconocimiento y evolución."
    },
    {
      id: 2,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/468bfb2-8ba3-5fea-7d18-cf7b5bebbaca_icono-5.webp",
      title: "**LECTURA INTEGRAL DE LA CARTA NATAL**",
      description: "Aprenderás a reconocer desafíos, talentos y aprendizajes, y a vincular la carta con la **historia personal y familiar** de quien consulta."
    },
    {
      id: 3,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/14efa1-a483-820d-bf4-0aec0853dc_icono-6.webp",
      title: "**AUTOCONOCIMIENTO**",
      description: "En esta Formación obtendrás las herramientas para tu **propia sanación, comprensión interna y expansión de consciencia.**"
    },
    {
      id: 4,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/ebb043-260d-4f78-bb46-40132577163_icono-4.webp",
      title: "**ASTROLOGÍA INFANTIL**",
      description: "Incorporarás las claves para interpretar Cartas Natales de niñ@s y orientar a padres y educadores en su acompañamiento."
    },
    {
      id: 5,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/5ae73a5-570-e874-8df7-5b0b35baa502_iconos-2.webp",
      title: "**ESENCIAS FLORALES Y ARQUETIPOS**",
      description: "Conocerás las **esencias florales vinculadas a signos y planetas**, y su uso en procesos emocionales y de transformación."
    },
    {
      id: 6,
      image: "https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/b3ad3-750a-8b41-3705-3a251f372b81_iconos-3.webp",
      title: "**CERTIFICACIÓN FINAL**",
      description: "Al completar la formación y tu trabajo final, recibirás tu **certificado como Astrólog@** por nuestra Escuela Astroterapéutica."
    }
  ];

  return (
    <div 
      className="mt-12 py-16 px-6 rounded-2xl"
      style={{
        backgroundImage: "url('https://kajabi-storefronts-production.kajabi-cdn.com/kajabi-storefronts-production/file-uploads/themes/2151875569/settings_images/246c482-2f1a-3131-cfbd-6c82f114bd61_formacion-astrologia-detalles-bg.webp')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        fontFamily: "'DM Sans', sans-serif"
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
          
          .certification-card {
            transition: all 0.3s ease-in-out;
          }
          
          .certification-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          }
          
          .certification-icon {
            transition: transform 0.3s ease-in-out;
          }
          
          .certification-card:hover .certification-icon {
            transform: scale(1.1);
          }
        `}
      </style>

      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-3xl md:text-3xl mb-3 font-bold text-white opacity-90">
          {renderFormattedText("**¡Transforma tu vida y certifícate como Astrólog@!**")}
        </h1>
        <p className="text-xl text-white opacity-90">
          Aprende Astrología y acompaña procesos de sanación y transformación.
        </p>
      </div>

      {/* Grid de certificaciones */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {certificationBlocks.map((block) => (
          <div
            key={block.id}
            className="certification-card bg-white rounded-2xl p-8 text-center shadow-lg"
          >
            {/* Imagen/Ícono */}
            <div className="certification-icon mb-6 flex justify-center">
              <img
                src={block.image}
                alt={`Ícono ${block.id}`}
                className="w-16 h-16 object-contain"
              />
            </div>

            {/* Título */}
            <h3 
              className="text-lg font-bold mb-4"
              style={{ color: '#5137d9' }}
            >
              {renderFormattedText(block.title)}
            </h3>

            {/* Descripción */}
            <p className="text-gray-800 text-base leading-relaxed">
              {renderFormattedText(block.description)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};


  const ExpandableBlocks = () => {
  const [expandedBlocks, setExpandedBlocks] = useState({});

  const toggleBlock = (blockId) => {
    setExpandedBlocks(prev => ({
      ...prev,
      [blockId]: !prev[blockId]
    }));
  };

  const blocks = [
    {
      id: 1,
      title: "Los fundamentos del lenguaje astrológico y el mundo emocional",
      subtitle: "PARTE I",
      content: [
        "**Introducción a la Astrología Psicológica y Evolutiva**",
        "**Introducción a la Carta Natal**",
        "**Introducción a los 4 Elementos**",
        "**Introducción al significado de Planetas, Signos y Casas**",
        "**Estudio del Sol – la identidad personal**",
        "**Estudio de la Luna – el mundo emocional**",
        "**La luna, Madre, Hogar y Linaje**",
        "**La Luna y el niño interno**",
        "**Formas de vivir las emociones dependiendo del Mecanismo Lunar**",
        "**Mecanismo lunar y apegos emocionales**",
        "**Terapia Floral y Astrología**",
        "**Trabajo terapéutico con niños**",
        "**Planetas Regentes**",
        "**Instrucciones dolorosas y sus dones**",
        "**Pasos para levantar la carta natal**"
      ],
      color: "border-blue-400 bg-blue-50"
    },
    {
      id: 2,
      title: "Instrucciones lunares a través de los signos y planetas",
      subtitle: "PARTE II",
      content: [
        "**Estudio de los 12 signos del zodiaco**",
        "**Estudio detallado de todos los planetas**",
        "**Estudio del Sol en los 12 signos**",
        "**Estudio de la Luna en los 12 signos**",
        "**Instrucciones lunares – planetas asociados a la Luna**",
        "**Estudio Dignidades, planetas en domicilio, exaltados, exilio y caída**"
      ],
      color: "border-purple-400 bg-purple-50"
    },
    {
      id: 3,
      title: "Quirón, aspectos al sol, el mundo emocional y los 4 elementos",
      subtitle: "PARTE III",
      content: [
        "**Significado de los 4 elementos en la carta natal**",
        "**Elemento dominante y elemento en déficit**",
        "**Clases prácticas de Mecanismo Lunar y Elementos**",
        "**Estudio de los principales aspectos astrológicos**",
        "**Estudio de Quirón, el sanador herido**",
        "**Estudio de Quirón aspectado a los planetas**",
        "**Estudio del Sol aspectado a los planetas**",
        "**El Sol y el Padre**"
      ],
      color: "border-green-400 bg-green-50"
    },
    {
      id: 4,
      title: "Casas, Quirón y luna por casas, temas y practica avanzadas, y sanación de las instrucciones lunares",
      subtitle: "PARTE IV",
      content: [
        "**Estudio detallado de las 12 Casas**",
        "**Estudio de la Luna en las 12 Casas**",
        "**Estudio de Quirón en las 12 Casas**",
        "**Estudio del Ascendente**",
        "**Estudio del Ascendente en los 12 signos**",
        "**Clase práctica avanzada Mecanismo Lunar**",
        "**Clase práctica interpretación Sol, Luna y Ascendente**",
        "**Clase práctica avanzada interpretación cartas**",
        "**Sanación Mecanismo Lunar en Niños y Adultos**"
      ],
      color: "border-orange-400 bg-orange-50"
    }
  ];

   // Función para renderizar texto con formato markdown básico
  
  
   return (
    <div className="mt-8 space-y-4 bloques">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
      
          
          .mt-8{
        margin-top: 4rem;
       
        }
          .expandable-block {
            transition: all 0.3s ease-in-out;
            font-family: 'DM Sans', sans-serif;
             margin-top:20px;
          }
          .expandable-content {
            transition: max-height 0.4s ease-in-out, opacity 0.3s ease-in-out;
            overflow: hidden;
          }
          .expandable-content.collapsed {
            max-height: 0;
            opacity: 0;
          }
          .expandable-content.expanded {
            max-height: 1000px;
            opacity: 1;
          }
          .arrow-icon {
            transition: transform 0.3s ease-in-out;
          }
          .arrow-icon.rotated {
            transform: rotate(90deg);
          }
          .expandable-block:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
          }
            .bloques{
           
            padding-left:60px;
             padding-right:60px;
            }
            .contenidostexto{
            text-align:center;
            padding-left:20px;
              padding-right:20px;
              margin-bottom:40px !important;
            }
             .contenidostexto p{
             color:#4A2472;
              fontFamily: "'DM Sans', sans-serif";
              margin-bottom:10px;
             }
              .clickmod{
              color:#3094af !important;
              text-align:center;
              }
              .flex-1 h4{
              color:#4A2472 !important;
              }
              .sub{
               color:#4A2472 !important;
              }
               .p-8{
                   margin-bottom: 4rem;
                   }
        `}
      </style>

      <h3 className="text-3xl md:text-3xl font-bold mb-3 clickmod">
        Contenidos de la Formación
      </h3>
      <div className='contenidostexto'>
      <p>Nuestra Formación es completamente asincrónica, lo que significa que <strong>puedes comenzar a estudiar en cualquier momento del año.</strong></p>
      
      <p>El programa se divide en <strong>cuatro partes</strong>, diseñadas para ofrecerte un avance gradual en el contenido. En total, contarás con 27 módulos que debes completar en orden.</p>
<p className='clickmod'>
<strong>¡Haz clic en los módulos para obtener más detalles!</strong>
</p>
</div>
      {blocks.map((block) => (
        <div
          key={block.id}
          className="expandable-block rounded-xl p-6 cursor-pointer bg-white shadow-md hover:shadow-lg"
          style={{ 
            border: '2px solid #62bfd8',
            fontFamily: "'DM Sans', sans-serif"
          }}
          onClick={() => toggleBlock(block.id)}
        >
          {/* Header del bloque */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-lg font-bold text-gray-800 mb-1">
                {renderFormattedText(block.title)}
              </h4>
              <p className="sub text-sm  text-gray-200 uppercase tracking-wider">
                {block.subtitle}
              </p>
            </div>
            
            {/* Flecha */}
            <div className={`arrow-icon ml-4 ${expandedBlocks[block.id] ? 'rotated' : ''}`}>
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                style={{ color: '#62bfd8' }}
              >
                <path 
                  d="M9 18L15 12L9 6" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          {/* Contenido desplegable */}
          <div className={`expandable-content mt-4 ${expandedBlocks[block.id] ? 'expanded' : 'collapsed'}`}>
            <div className="border-t border-gray-300 pt-4">
              <ul className="space-y-2">
                {block.content.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-gray-600 mr-3 mt-1 flex-shrink-0">•</span>
                    <span className="text-gray-700 leading-relaxed">
                      {renderFormattedText(item)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ))}
      <div className="text-center-botom">
        <a
          href="https://www.astroterapeutica.com/formacion-online-completa-descarga-del-programa"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: '15px',
               width: '60%',
    marginRight: '20%',
    marginLeft: '20%',
    marginTop: '2rem',
            fontWeight: 'bold',
            padding: '20px',
            backgroundColor: '#3094af',
            color: 'white',
            borderRadius: '50px',
            border: '0',
            display: 'inline-block',
            textDecoration: 'none',
            textAlign: 'center',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#3094af';
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#3094af';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          DESCARGAR PROGRAMA Y CALENDARIO
        </a>
      </div>
    </div>
  );
};

  // Función para determinar qué oferta corresponde según el score
  const getOfferType = (score) => {
    if (score >= 0 && score <= 6) {
      return 'ofertaA';
    } else if (score === 7)  {
      return 'ofertaB';
      } else if (score === 8)  {
      return 'ofertaC';
    } else if (score === 9) {
      return 'ofertaD';
    } else if (score === 10) {
      return 'ofertaG';
    }
    return null;
  };

  // Función para obtener la oferta desde Firebase
  const fetchOfferFromFirebase = async (offerType) => {
    try {
      const appId = 'examenes';
      const offerDocRef = doc(db, `artifacts/${appId}/ofertas/${offerType}`);
      const offerSnap = await getDoc(offerDocRef);
      
      if (offerSnap.exists()) {
        return offerSnap.data();
      } else {
        console.error(`No se encontró la oferta: ${offerType}`);
        return null;
      }
    } catch (error) {
      console.error("Error fetching offer:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchUserResults = async () => {
      if (!user || !db) return;
      
      const appId = 'examenes';
      const userDocRef = doc(db, `artifacts/${appId}/public/data/exam_results/${user.uid}`);
      
      try {
        const docSnap = await getDoc(userDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setResult(data);
          
          // Determinar el tipo de oferta según el score
          const offerType = getOfferType(data.score);
          
          if (offerType) {
            // Obtener la oferta desde Firebase
            const offerData = await fetchOfferFromFirebase(offerType);
            
            if (offerData) {
              setOffer(offerData);
           //    if (offerType === 'ofertaA') {
              const regionalData = await getRegionalPricing(user, db, offerType);
              setRegionalPricing(regionalData);
              console.log('🎯 Pricing regional:', regionalData);
            //}
              // Mostrar fuegos artificiales solo para puntuación perfecta (10)
              if (data.score === 10) {
                setShowFireworks(true);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching user results:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserResults();
  }, [user, db]);

const PricingBlock = ({ title, preText, price, ctaText, ctaUrl,ctaUrllg, isStrikethrough, showSecondaryButton }) => {
  const preTextClass = isStrikethrough ? 'pre-price-strikethrough' : 'pre-price-regular';

  return (
    <div className="pricing-block">
      <h3 className="pricing-title">{title}</h3>
      <hr className="separator" />
      <div className="pricing-details">
        {preText && <span className={preTextClass}>{preText}</span>}
        <div className="main-price">{price}</div>
      </div>
      <a href={ctaUrl} className="cta-link">
        <button className="cta-button">{ctaText}</button>
      </a>
      {/* Nuevo botón condicional */}
      {showSecondaryButton && (
        <a href={ctaUrllg} className="cta-link-secondary">
          <button className="cta-button-secondary">PAGAR CON TARJETA</button>
        </a>
      )}
    </div>
  );
};




 const renderCTAButton = () => {

const shouldShowSecondaryButton =  regionalPricing.countryCode === 'AR';
  // Si hay pricing regional (solo para ofertaA), usarlo
  const pricing = regionalPricing.pricing;
    if (offer.id === 'ofertaA' && regionalPricing && regionalPricing.pricing) {
      
      console.log("pricing",regionalPricing.countryCode);
        
    return (
      
      <div className="pricing-container">
         <style>
        {`
.p-8{
margin-bottom:4rem;
        }
        .cta-button-secondary {
 
 
width: 100%;
 background-color: #888; /* Color gris */
  color: white;
  border: none;
  border-radius: 50px;
  padding: 15px;
  font-size: 1em;
  font-weight: bold;
  cursor: pointer;
  margin-top: 20px;
}
         .pricing-container {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.pricing-block {
  display: flex;
  flex-direction: column;
 align-items: stretch;
  border: 2px solid #3094af;
  border-radius: 20px;
  background-color: white;
  padding: 20px;
  text-align: center;
  
  width: 100%;
}

.pricing-title {
  color: #3094af;
  font-size: 1.2em;
  font-weight: bold;
}

.separator {
  width: 80%;
  height: 1px;
  background-color: #ccc;
  border: none;
  margin: 10px 0;
  margin-left: 10%;
}

.pre-price {
  color: #888;
  text-decoration: line-through;
  font-size: 0.9em;
}

.main-price {
  font-size: 2.5em;
  font-weight: bold;
  color: black;
  margin-top: 5px;
}
.pre-price-strikethrough {
  color: #888;
  text-decoration: line-through;
  font-size: 0.9em;
}

.pre-price-regular {
  color: #888; /* O el color que desees para el texto sin tachar */
  font-size: 0.9em;
}
.cta-button {
  width: 100%;
  background-color: #4a2472;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 15px;
  font-size: 1em;
  font-weight: bold;
  cursor: pointer;
  margin-top: 20px;
}

/* Media Query para móvil: apila los bloques en pantallas más pequeñas */
@media (max-width: 768px) {
  .pricing-container {
    flex-direction: column;
    align-items: center;
  }
}
        `}
      </style>
        {/* Bloque de Pago Único */}
        <PricingBlock
          title="PAGO ÚNICO"
          preText={`Antes ${pricing.antes} USD`}
          price={`$ ${pricing.preciopu} USD`}
          ctaText={offer.cta || 'PAGAR'}
          ctaUrl={pricing.urlpukj}
           ctaUrllg={pricing.urllgpu}
          isStrikethrough={true}
          showSecondaryButton={shouldShowSecondaryButton} // Pasamos la propiedad
        />
        
        {/* Bloque de 3 Pagos Mensuales */}
        {pricing.preciofinal && pricing.preciomp && (
          <PricingBlock
            title="3 PAGOS MENSUALES"
            preText={pricing.preciofinal}
            price={`$ ${pricing.preciomp}`}
            ctaText={offer.cta || 'PAGAR'}
            ctaUrl={pricing.urlmpkj}
             ctaUrllg={pricing.urllgmp}
            isStrikethrough={false}
            showSecondaryButton={shouldShowSecondaryButton} // Pasamos la propiedad
          />
        )}
      </div>
    );
  }
    // Fallback al botón original para otras ofertas
    if (offer.link && offer.cta) {
      return (
       
        
         <div className="pricing-container">

 <div 
      className="mt-12 py-16 px-6"
      style={{
        fontFamily: "'DM Sans', sans-serif"
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap');
        `}
      </style>

      <div className="max-w-4xl mx-auto text-center">
        {/* Cita principal en cursiva */}
    

        {/* Información sobre duración */}
        <div className="mb-6">
          <p 
            className="text-3xl md:text-3xl  mb-3"
            style={{ color: '#bf965e' }}
          >
        <strong>FORMACIÓN PREMIUM AVANZADA</strong>
          </p>
        </div>

        {/* Información sobre webinars */}
        <div>
          <p 
            className="text-lg "
          
          >
            Fórmate como astrólogo bajo la mirada Psicológica Evolutiva
          </p>
          <p   style={{ color: '#4A2472' }}><strong>Online y en vivo con Pablo Flores</strong></p>
<p   style={{ color: '#4A2472' }}><strong>Fecha de inicio: Septiembre 2025</strong></p>
        </div>
      </div>
    </div>

      <style>
        {`
          .py-16{
            padding-top: 2rem;
            padding-bottom:2rem;
          }
          .p-8{
            margin-bottom:4rem;
          }
          .cta-button-secondary {
            width: 100%;
            background-color: #888;
            color: white;
            border: none;
            border-radius: 50px;
            padding: 15px;
            font-size: 1em;
            font-weight: bold;
            cursor: pointer;
            margin-top: 20px;
          }
          .pricing-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-top: 0;
            margin-bottom: 2rem;
          }
          .pricing-block {
           display: flex;
    align-items: center;
    flex-direction: column;
            border: 2px solid #bf965e;
            border-radius: 20px;
            background-color: white;
            padding: 20px;
            text-align: center;
            width:auto;
          }
          .pricing-title {
            color: #bf965e;
            font-size: 1.2em;
            font-weight: bold;
          }
          .separator {
            width: 80%;
            height: 1px;
            background-color: #ccc;
            border: none;
            margin: 10px 0;
          }
          .pre-price {
            color: #888;
            text-decoration: line-through;
            font-size: 0.9em;
          }
          .main-price {
            font-size: 2.5em;
            font-weight: bold;
            color: black;
            margin-top: 5px;
          }
          .pre-price-strikethrough {
            color: #888;
            text-decoration: line-through;
            font-size: 0.9em;
          }
          .pre-price-regular {
            color: #888;
            font-size: 0.9em;
          }
          .cta-button {
            width: 100%;
            background-color: #bf965e;
            color: white;
            border: none;
            border-radius: 50px;
            padding: 15px;
            font-size: 1em;
            font-weight: bold;
            cursor: pointer;
            margin-top: 20px;
          }

          /* Estilos para los nuevos bloques de formación */
          .formation-pricing-container {
            display: flex;
            gap: 20px;
            justify-content: center;
               width: 80%;
            margin: 0 auto;
            margin-top: 30px;
          }
          
          .formation-block {
            flex: 1;
            border: 2px solid #bf965e;
            border-radius: 20px;
            background-color: white;
            padding: 20px;
            text-align: center;
          }
          
          .formation-title {
            color: #bf965e;
            font-size: 1.2em;
            font-weight: bold;
            margin-bottom: 10px;
          }
          
          .formation-separator {
            width: 80%;
            height: 1px;
            background-color: #ccc;
            border: none;
            margin: 10px auto;
          }
          
          .formation-price {
            font-size: 1.5em;
            font-weight: bold;
            color: black;
            margin-top: 10px;
          }

          /* Media Query para móvil */
          @media (max-width: 768px) {
            .pricing-container {
              flex-direction: column;
              align-items: center;
            }
            
            .formation-pricing-container {
              flex-direction: column;
              gap: 15px;
            }
          }
        `}
      </style>
        {/* Bloque de Pago Único */}
        <PricingBlock
          title="MATRÍCULA DE PRE-INSCRIPCIÓN"
          preText={`PARA RESERVAR TU CUPO`}
          price={`$ 150 USD`}
          ctaText={offer.cta || 'PAGAR'}
          ctaUrl={offer.link}
           ctaUrllg={pricing.urllgpu}
          isStrikethrough={false}
          showSecondaryButton={shouldShowSecondaryButton} // Pasamos la propiedad
        />
        
         {/* Texto explicativo */}
      <div className="text-center mt-8 mb-6">
        <p className="text-black mb-4">
          La matrícula es adicional al valor de la Formación, y no es reembolsable, ni canjeable por otro curso.
        </p>
        <h3 className="text-xl font-bold" style={{ color: '#bf965e' }}>
          VALOR DE LA FORMACIÓN Y FORMAS DE PAGO CON {offer.discount}
        </h3>
      </div>

      {/* Bloques de formación */}
      <div className="formation-pricing-container">
        {/* Bloque Pago Único */}
        <div className="formation-block">
          <div className="formation-title">PAGO ÚNICO</div>
          <hr className="formation-separator" />
          <div className="pricing-details"><span className="pre-price-strikethrough">{offer.antes}</span></div>
          <div className="formation-price">{offer.precio}</div>
        </div>

        {/* Bloque En 10 Pagos */}
        <div className="formation-block">
          <div className="formation-title">EN 10 PAGOS</div>
          <hr className="formation-separator" />
          <div className="pricing-details"><span className="pre-price-strikethrough">{offer.antesmp}</span></div>
          <div className="formation-price">{offer.preciomp}</div>
        </div>
      </div>
        
      </div>
      );
    
    
    
    
    }
    
    return null;
  };


  
 const handleFireworksComplete = () => {
    setShowFireworks(false);
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl text-gray-600">Cargando tus resultados...</div>
      </div>
    );
  }

  if (!result || !offer) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-100">
        <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Aún no has realizado el examen.</h2>
          <p className="text-lg text-gray-600 mb-6">Por favor, regresa a la página de inicio para comenzar.</p>
         
        </div>
      </div>
    );
  }

return (
    <div className="flex flex-col items-center min-h-screen bg-gray-100 pt-28">
      <style>
        {`
          @keyframes drawCircle {
            from {
              stroke-dashoffset: ${2 * Math.PI * (80 - 24)};
            }
            to {
              stroke-dashoffset: ${2 * Math.PI * (80 - 24) - ((result?.score || 0) / 10) * 2 * Math.PI * (80 - 24)};
            }
          }
        `}
      </style>
     
       <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-6xl">
      
        <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">Resultados del Examen</h2>
        
        {/* Gráfico de torta animado */}
        <div className="mb-8 flex justify-center">
          <AnimatedPieChart score={result.score} total={10} />
        </div>
        
        {/* Contenido de la oferta con campos separados */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-xl shadow-inner border border-blue-100">
          {/* Header/Encabezado */}
          {offer.header && (
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              {renderFormattedText(offer.header)}
            </h3>
          )}
          
          {/* Mensaje motivador */}
          {offer.motivationalMessage && (
            <p className="text-lg text-gray-700 mb-6 leading-relaxed text-center">
              {renderFormattedText(offer.motivationalMessage)}
            </p>
          )}
          
          {/* Título de la oferta */}
          {offer.offerTitle && (
            <div className="text-center mb-6">
              <p className="text-xl text-gray-800 mb-2">
               {offer.tag}
               
              </p>
            </div>
          )}
          
          {/* Features/Características */}
          {offer.features && offer.features.length > 0 && (
            <div className="mb-6">
              <ul className="space-y-3">
                {offer.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-600 mr-3 mt-1">•</span>
                    <span className="text-gray-700 text-lg">
                      {renderFormattedText(feature)}
                    </span>
                  </li>
                ))}
              </ul>
               
            </div>
          )}
           <p className="text-xl text-gray-800 mb-2">
          {offer.ad}
           </p>
          {/* Botón CTA */}
         {renderCTAButton()}
        </div>
        {/* ✅ NUEVO: Bloques desplegables solo para ofertaA */}
{getOfferType(result.score) === 'ofertaA' ? (
  <>
    <FinalImageSection />
    <TestimonialSection />
    <BenefitsSection />
    <SupportSection />
    <CertificationSection />
    <ExpandableBlocks />
    <TestimonialSectionofer />
  </>
) : (
  <>
    {/* Aquí van las secciones que quieres mostrar si NO es 'ofertaA' */}
      <FinalImageSectionav />
       <TestimonialSectionav />
        <ContenidosAv />
           <DesadorroAv/>
         <MetodoAv />
          <AprenderasAv />
        
         
  
  </>
)}
        {/* Iframe (opcional, puede quitarse si no se necesita) */}
        {renderCTAButton()}
      </div>
      
      {showFireworks && <FireworksComponent onComplete={handleFireworksComplete} />}
    </div>
  );
};

// Componente de la página de instrucciones
const InstructionsPage = ({ onStartExam }) => {
  const [isTicketChecked, setIsTicketChecked] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Ambos checkboxes deben estar marcados para habilitar el botón
  const canStartExam = isTicketChecked && termsAccepted;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-100">
      <style>
        {`
          .max-w-md {
    max-width: 35rem;
        }
    .checks{
    display: flex
;
    flex-direction: column;
    align-items: flex-start;
    }
    .w-5 {
    width: auto;
}
`}
      </style>
      
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Instrucciones del Examen</h2>
        <ul className="list-disc list-inside text-left text-lg text-gray-700 space-y-2 mb-6">
          <li>1️⃣ Responde 10 preguntas de astrología (nivel creciente).</li>
          <li>2️⃣ Tienes 3 minutos para cada respuesta.</li>
          <li>3️⃣ El test se muestra 1 pregunta por vez (no puedes retroceder).</li>
          <li>4️⃣ Si sales de la pantalla, el test se reinicia desde cero.</li>
          <li>5️⃣ Al final descubrirás tu resultado y tu premio inmediato.</li>
        </ul>
        <div className="checks">
        {/* Primer checkbox - Instrucciones */}
        <div className="flex items-center justify-center mb-4">
          <input
            type="checkbox"
            id="instructions-ticket"
            checked={isTicketChecked}
            onChange={(e) => setIsTicketChecked(e.target.checked)}
            className="w-5 h-5 text-blue-600 bg-gray-100 rounded border-gray-300 focus:ring-blue-500"
          />
          <label htmlFor="instructions-ticket" className="ml-2 text-lg font-medium text-gray-900">
            He leído todas las instrucciones
          </label>
        </div>

        {/* Segundo checkbox - Términos y Condiciones */}
        <div className="flex items-center justify-center mb-4">
          <input
            type="checkbox"
            id="terms-ticket"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="w-5 h-5 text-blue-600 bg-gray-100 rounded border-gray-300 focus:ring-blue-500"
          />
          <label htmlFor="terms-ticket" className="ml-2 text-lg font-medium text-gray-900">
            He leído y acepto los{' '}
            <a
              href="https://www.astroterapeutica.com/beca-bases-y-condiciones-2025"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-800 transition-colors"
            >
              términos y condiciones de uso
            </a>
          </label>
        </div>
</div>
        <button
          onClick={onStartExam}
          disabled={!canStartExam}
          className={`py-3 px-8 font-bold rounded-lg shadow-md transition-colors transform ${
            canStartExam
              ? 'bg-blue-600 text-white hover:bg-blue-700 hover:scale-105'
              : 'bg-gray-400 text-gray-700 cursor-not-allowed'
          }`}
        >
          Comenzar Examen
        </button>
      </div>
    </div>
  );
};

const renderTextWithFormatting = (text) => {
  // Simple markdown-like parser for bold text and line breaks
 const lines = text.split("\n").filter(line => line.trim() !== "");

  return lines.map((line, index) => {
    const hasBold = /\*\*(.*?)\*\*/.test(line);
    const formatted = line
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'); // reemplaza **texto**
    
    return (
      <p
        key={index}
        className={hasBold ? "text-xl font-bold mb-2" : "text-base text-gray-700 mb-1"}
        dangerouslySetInnerHTML={{ __html: formatted }}
      />
    );
  });
};
 // Reemplaza los valores con la configuración de tu proyecto de Firebase
     const firebaseConfig = {
  apiKey: "AIzaSyAzFkQaOC5f2Ye3NrL3BDZfzSKCMNlgdNs",
  authDomain: "astroterapeutica-examen.firebaseapp.com",
  databaseURL: "https://astroterapeutica-examen-default-rtdb.firebaseio.com",
  projectId: "astroterapeutica-examen",
  storageBucket: "astroterapeutica-examen.firebasestorage.app",
  messagingSenderId: "527604557090",
  appId: "1:527604557090:web:d1f5ece93e0acf8e7ff17b",
  measurementId: "G-29C85LDB3P"
};

// Componente para la animación de fuegos artificiales
const FireworksComponent = ({ onComplete }) => {
 const containerRef = useRef(null);
  const fireworksRef = useRef(null);
const [isFading, setIsFading] = useState(false);
  useEffect(() => {
    // Carga la librería de fuegos artificiales desde una CDN.
    // Esto evita el error de "módulo no encontrado" en el entorno de React.
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/fireworks-js@2.10.1/dist/index.umd.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (containerRef.current && window.Fireworks) {
        // Configuración de los fuegos artificiales
        const options = {
          autoresize: true,
          opacity: 0.5,
          acceleration: 1.05,
          friction: 0.97,
          gravity: 1.5,
          particles: 50,
          traceLength: 3,
          traceSpeed: 10,
          explosion: 5,
          intensity: 30,
          flickering: 50,
          lineStyle: 'round',
          hue: {
            min: 0,
            max: 360
          },
          delay: {
            min: 30,
            max: 60
          },
          rocketsPoint: {
            min: 50,
            max: 50
          },
          lineWidth: {
            explosion: {
              min: 1,
              max: 3
            },
            trace: {
              min: 1,
              max: 2
            }
          },
          brightness: {
            min: 50,
            max: 80
          },
          decay: {
            min: 0.015,
            max: 0.03
          },
          mouse: {
            click: false,
            move: false,
            max: 1
          }
        };

        // La librería expone una clase global 'Fireworks'
        const fireworks = new window.Fireworks.default(containerRef.current, options);
        fireworksRef.current = fireworks;
        fireworks.start();
      }
    };
     // Inicia el desvanecimiento después de 2.5 segundos
    const fadeTimer = setTimeout(() => {
      setIsFading(true);
    }, 6000);

    // Oculta el componente completamente después de 3 segundos
    const completeTimer = setTimeout(() => {
      if (onComplete) {
        onComplete();
      }
    }, 10000);

    // Limpieza
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
      if (fireworksRef.current) {
        fireworksRef.current.stop();
        fireworksRef.current.clear();
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [onComplete]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 100,
        pointerEvents: 'none',
        opacity: isFading ? 0 : 1,
        transition: 'opacity 0.5s ease-in-out'
      }}
    />
  );
};



const QuizApp = () => {
  const [user, setUser] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [db, setDb] = useState(null);
  const [view, setView] = useState('loading'); // 'loading', 'login', 'instructions', 'exam', 'userResults', 'results'
 const [showFireworks, setShowFireworks] = useState(false); 
  const [showWarningModal, setShowWarningModal] = useState(false);
   const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [examState, setExamState] = useState({
    currentQuestionIndex: 0,
    userAnswers: [],
    questions: [],
    examAttempts: 0,
    isExamActive: false,
    timer: 180,
  });

  const timerRef = useRef(null);
  const lastAnswerRef = useRef(null);
 useExamSessionGuard({ 
    db, 
    user, 
    examState, 
    setView, 
    firebaseConfig, 
    setShowWarningModal 
  });
  // Configuración de las preguntas y respuestas
  const configurableQuestions = [
    {
       originalIndex: 0,
       question: {
        text: "**Respecto a la Luna en la carta natal, ¿Cuáles de las siguientes afirmaciones son correctas?**\n\n\nI. Representa nuestras emociones, necesidades básicas y el vínculo con la madre o figuras de cuidado.\n\nII. Su signo y casa muestran cómo buscamos seguridad emocional.\n\nIII. La Luna nunca cambia de signo en la carta natal, pero sí cambia cada dos días en el cielo.\nIV. Su fase natal no influye en el temperamento de la persona.",
      },
      options: [
        "a) I, II y III son correctas.",
        "b) II y III son correctas.",
        "c) I, II y IV son correctas.",
        "d) Todas son correctas."
      ],
      correctAnswer: "a) I, II y III son correctas."
    },
    {
      originalIndex: 1,
      question: "**En relación con los aspectos astrológicos, ¿cuáles de las siguientes afirmaciones son verdaderas?**\n\n\nI. Una oposición ocurre cuando dos planetas están a 180° y suele generar tensión polarizante.\n\nII. Una cuadratura es un aspecto de 90° y se asocia a desafíos y fricción.\n\nIII. El sextil es un aspecto de 120° y siempre representa dificultad.\n\nIV. El trígono es un aspecto de 120° que indica fluidez y facilidad entre planetas.",
      options: ["a) I y II son correctas.", "b) II y III son correctas.", "c) I, II y IV son correctas.", "d) Todas son correctas."],
      correctAnswer: "c) I, II y IV son correctas."
    },
    {
      originalIndex: 2,
      question: "**¿Por qué nos es tan difícil entender y experimentar el dolor que representa Quirón en astrología?**\n\n\nI. Porque se activan las capas de protección del ego, la Luna, Plutón y Saturno para resguardarnos e impedir que lo experimentemos.\n\nII. Porque se activan las capas de protección del ego, la Luna y Saturno para resguardarnos e impedir que lo experimentemos.\n\nIII. Porque representa el dolor de encarnar en este mundo, y no recordamos haberlo elegido.\n\nIV. Porque la tendencia del ser humano es vivir buscando el placer, tratando de evitar el sufrimiento.",
      options: ["a) II y IV son correctas.", "b) III y IV son correctas.", "c) I, III y IV son correctas.", "d) II, III y IV son correctas."],
      correctAnswer: "c) I, III y IV son correctas."
    },
    {
      originalIndex: 3,
     question: "**Respecto de una conjunción Venus/Plutón en Sagitario, ¿Cuáles de los siguientes enunciados son correctos?**\n\n\nI. Las experiencias negativas vividas en antiguas relaciones teñirán las relaciones nuevas que se inicien.\n\nII. La persona probablemente oscilará entre el optimismo y la negatividad en sus relaciones.\n\nIII. La positividad y carácter benéfico de Sagitario hará más fácil la integración de la sombra.\n\nIV. La persona tendrá que trabajar sus miedos relacionales incluso si sus relaciones son satisfactorias.",
      options: ["a) II y IV", "b) I, II y IV", "c) I, II y III", "d) Todas son correctas."],
      correctAnswer: "b) I, II y IV"
    },
    {
      originalIndex: 4,
      question: "**El Sol representa el punto evolutivo de la carta que nos habla de la identidad que venimos a desarrollar a lo largo de nuestra vida para expresarnos desde un nivel de consciencia cada vez más elevado. Indica cuáles de las siguientes expresiones no son correctas:**\n\n\nI. Los niños pequeños suelen expresar la energía solar de manera libre y espontánea, pero con el paso del tiempo se van limitando por los condicionamientos de su entorno.\n\nII. La manera en que expresamos nuestra energía solar no se modifica a lo largo del tiempo.\n\nIII. A las personas con Sol en Casa 12 suelen sentirse más inspiradas a la hora de manifestar su identidad y les resulta más sencillo conectar con su esencia.\n\nIV. La Luna, Saturno y Quirón pueden condicionar fuertemente la expresión de la identidad solar.",
      options: ["a) Solo I", "b) I y IV", "c) II y IV", "d) II y III"],
      correctAnswer: "d) II y III"
    },
    {
      originalIndex: 5,
      question: "**En relación con los signos, ¿Cuál de las siguientes aseveraciones es FALSA?**\n\n\nI. Tauro se siente seguro cuando percibe sus sentidos, goza de ellos y se encuentra frente a cambios constantes. El dolor taurino se relaciona con el miedo a perder el control de sus pertenencias y se mueve rápidamente, con valentía y sin dudar.\n\nII. Realizar un aporte a la humanidad está únicamente relacionado con el signo de Acuario, ya que este tiene como finalidad aportar y ayudar al colectivo y al planeta.\n\nIII. Es más probable que un pisciano se polarice a Virgo y no al revés porque Piscis es más difícil de sostener psicológicamente y Virgo tiene más conciencia corporal que Piscis.\n\nIV. Sagitario representa la búsqueda de sentido y de identidad que surge luego de la crisis sufrida cuando lo que era fundamental para nuestra vida, así como nuestras prioridades y apegos, ya no nos representan.",
      options: ["a) Solo II", "b) II y III", "c) I y II", "d) I, II y IV"],
      correctAnswer: "c) I y II"
    },
    {
      originalIndex: 6,
      question: "**¿Qué implica la frase “el Ascendente como máscara”?**",
      options: ["a) Que la persona utiliza la energía del ascendente como una protección para no dar a conocer su verdadero yo.", "b) Que es la energía que la persona usa exclusivamente para lidiar con los problemas que le trae la realidad.", "c) Que el signo del Ascendente es una barrera que nos impide conectar con la realidad.", "d) Que las características del signo del Ascendente son percibidas por los demás, pero no siempre por nosotros mismos."],
      correctAnswer: "d) Que las características del signo del Ascendente son percibidas por los demás, pero no siempre por nosotros mismos."
    },
    {
      originalIndex: 7,
      question: "**En relación con los planetas, ¿Cuál de las siguientes afirmaciones es VERDADERA?**\n\n\nI. Al igual que Saturno, Urano marca septenios en la vida de la persona, que se caracterizan por procesos evolutivos personales, momentos de desarrollo en que se alcanza un aprendizaje específico para la edad, se enfrían las emociones, se modifica el cuerpo, se sensibiliza la mente y se asume la conciencia de nuevos retos.\n\nII. Saturno era conocido como el 'Gran maléfico' en la astrología clásica predictiva. En la astrología psicológica se le conoce como el 'Gran benéfico' porque lleva de la mano a la persona para perder el miedo al cambio necesario para alcanzar una nueva etapa de su vida.\n\nIII. Neptuno aspectado a Mercurio altera la forma en que funciona la mente e induce una mayor capacidad para percibir las corrientes ocultas y los matices afectivos del medio, lo que permite a la persona tener un enfoque más claro y racional de la vida.\n\nIV. La conjunción de Marte con Venus exalta las capacidades sensuales y amorosas de la persona, y disminuye la existencia de conflictos internos y externos al imbuir a Marte de la paz de Venus, a la vez que activa la cualidad creativa de Venus gracias a la voluntad de Marte.",
      options: ["a) Solo II", "b) II y III", "c) I, III y IV", "d) I y IV"],
      correctAnswer: "d) I y IV"
    },
    {
      originalIndex: 8,
      question: "**Respecto de una conjunción Marte/Neptuno en Capricornio, opuesta a Saturno en Cáncer, ¿Cuáles de los siguientes enunciados son correctos?**\n\n\nI. Marte conjunto a Neptuno le otorga el don de moverse de modo sensible y conectado con las necesidades de otras personas.\n\nII. Representa un conflicto entre el moverse en mundo interno intangible versus la necesidad de actuar en la realidad.\n\nIII. Probablemente la persona será muy trabajadora, ya que en un polo está Capricornio y en el otro polo está Saturno.\n\nIV. La configuración le dificulta a la persona sentirse satisfecha de los resultados de sus propias acciones.",
      options: ["a) Todas son correctas.", "b) I, II y IV", "c) I y II", "d) II y IV"],
      correctAnswer: "d) II y IV"
    },
    {
      originalIndex: 9,
      question: "**Si una persona tiene a la Luna en Géminis en la Casa II en conjunción a Neptuno y cuadratura a Saturno, ¿De qué forma se manifiesta el mecanismo lunar?**",
      options: ["a) La persona necesita poner en palabras sus emociones y siente que los demás no pueden contenerla porque no comprenden su lenguaje.", "b) La persona puede ser muy introvertida y tener mucha dificultad para expresar lo que realmente siente.", "c) La persona puede desarrollar una capacidad superior de comunicar su mundo interno y eso le puede ayudar a generar abundancia si trabaja con sus limitaciones.", "d) La persona se siente no amada por los demás y eso la lleva a escapar a un mundo de fantasía donde dialoga consigo misma de forma interna.","e) Todas las afirmaciones son ciertas.","f) Ninguna de las afirmaciones es cierta."],
      correctAnswer: "e) Todas las afirmaciones son ciertas."
    },
  ];

   const handleWarningAccept = () => {
    setShowWarningModal(false);
    setView('instructions'); // Llevar de vuelta a las instrucciones
  };

   const checkUserAttempts = async (user, db) => {
    const appId = 'examenes';
    const userDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/user_data/exam_status`);
    
    try {
      const docSnap = await getDoc(userDocRef);
      if (docSnap.exists()) {
       const attempts = docSnap.exists() ? docSnap.data().attempts : 0;
        console.log("aa",attempts);
        return attempts >= 2; // MAX_ATTEMPTS = 2
      }
      return false;
    } catch (error) {
      console.error("Error checking user attempts:", error);
      return false;
    }
  };
  // Lógica de inicialización de Firebase y autenticación
  useEffect(() => {
    try {
     
      const app = initializeApp(firebaseConfig);
      const auth = getAuth(app);
      const firestoreDb = getFirestore(app);
      setDb(firestoreDb);

      const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
        if (authUser) {
          setUser(authUser);
          if (authUser.email === 'soporte@astroterapeutica.com') {
            setView('results');
          } else {
             const hasExceededAttempts = await checkUserAttempts(authUser, firestoreDb);
             console.log("hasExceededAttempts",hasExceededAttempts);
        
               if (hasExceededAttempts) {
              setView('sessionExpired');
              return;
            }
            // Cargar el estado del examen del usuario no-admin
            const appId = 'examenes';
            const userDocRef = doc(firestoreDb, `artifacts/${appId}/users/${authUser.uid}/user_data/exam_status`);
            const docSnap = await getDoc(userDocRef);
             
            if (docSnap.exists()) {
              const attempts = docSnap.data().attempts;
              if (attempts >= 1) { // 1 intento permitido
                // Verificar si tiene resultados
                const resultsDocRef = doc(firestoreDb, `artifacts/${appId}/public/data/exam_results/${authUser.uid}`);
                const resultsSnap = await getDoc(resultsDocRef);
                
                if (resultsSnap.exists()) {
                  setView('userResults');
                } else {
                  setView('instructions');
                }
              } else {
                setView('instructions');
              }
            } else {
              // Si el usuario es nuevo, mostrar las instrucciones
              setView('instructions');
            }
          }
        } else {
          setView('login');
        }
        setIsAuthReady(true);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error("Error initializing Firebase:", error);
      setIsAuthReady(true);
    }
  }, []);

  // Lógica para el temporizador del examen
useEffect(() => {
  if (!examState.isExamActive) {
    if (timerRef.current) clearInterval(timerRef.current);
    return;
  }

  if (timerRef.current) clearInterval(timerRef.current);

  timerRef.current = setInterval(() => {
    setExamState(prev => {
      const newTimer = prev.timer - 1;
      if (newTimer <= 0) {
        clearInterval(timerRef.current);
        
        // ✅ Todo en una sola operación de estado
        const newUserAnswers = [...prev.userAnswers];
        newUserAnswers[prev.currentQuestionIndex] = selectedAnswer;
        const nextIndex = prev.currentQuestionIndex + 1;
        
        if (nextIndex < prev.questions.length) {
          // Avanzar a siguiente pregunta
          setTimeout(() => setSelectedAnswer(null), 0); // Resetear después del render
          return { 
            ...prev, 
            currentQuestionIndex: nextIndex, 
            userAnswers: newUserAnswers, 
            timer: 180 
          };
        } else {
          // Finalizar examen
          finishExam(newUserAnswers);
          return { ...prev, isExamActive: false };
        }
      }
      return { ...prev, timer: newTimer };
    });
  }, 1000);

  return () => {
    if (timerRef.current) clearInterval(timerRef.current);
  };
}, [examState.isExamActive, examState.currentQuestionIndex, selectedAnswer]);
 
useEffect(() => {
    setSelectedAnswer(null);
  }, [examState.currentQuestionIndex]);

 
  // Lógica de reinicio del examen y del temporizador
  const startExam = useCallback(async () => {
    if (!user) {
      setView('login');
      return;
    }

    const appId = 'examenes';
    const userDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/user_data/exam_status`);
    const docSnap = await getDoc(userDocRef);
    const attempts = docSnap.exists() ? docSnap.data().attempts : 0;
    
    if (attempts >= 1) {
        setView('sessionExpired');
        return;
    }

    const shuffledQuestions = [...configurableQuestions].sort(() => Math.random() - 0.5);
    setExamState(prev => ({
      ...prev,
      questions: shuffledQuestions,
      currentQuestionIndex: 0,
      userAnswers: Array(shuffledQuestions.length).fill(null),
      isExamActive: true,
      timer: 180,
    }));
    setView('exam');
    lastAnswerRef.current = null;
  }, [user, configurableQuestions, db]);

  // Maneja el avance a la siguiente pregunta
   const handleNextQuestion = useCallback(() => {
    setExamState(prev => {
      const newUserAnswers = [...prev.userAnswers];
      newUserAnswers[prev.currentQuestionIndex] = selectedAnswer;
      const nextIndex = prev.currentQuestionIndex + 1;
      
      if (nextIndex < prev.questions.length) {
        return { ...prev, currentQuestionIndex: nextIndex, userAnswers: newUserAnswers, timer: 180 };
      } else {
        finishExam(newUserAnswers);
        return { ...prev, isExamActive: false };
      }
    });
  }, [selectedAnswer, examState.questions.length]);

  // Lógica para cuando el usuario selecciona una respuesta
  const handleAnswerClick = (answer) => {
    lastAnswerRef.current = answer;
    handleNextQuestion(answer);
  };
const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
  };

  const webhookSentRef = useRef(false);
  // Función para guardar los resultados del examen
  const finishExam = async (finalAnswers) => {
    
    if (webhookSentRef.current) {
    console.log('⚠️ Webhook ya enviado, saltando...');
    return;
  }
    
    const appId = 'examenes';
    const userId = user.uid;
    const userName = user.displayName || 'Usuario Desconocido';

 

    let score = 0;
    const answersWithCorrectness = finalAnswers.map((answer, index) => {
      const question = examState.questions[index];
      const isCorrect = answer === question.correctAnswer;
      if (isCorrect) score++;
      return {
        question: question.originalIndex + 1,
        userAnswer: answer,
        correctAnswer: question.correctAnswer,
        isCorrect: isCorrect,
        originalIndex: question.originalIndex
      };
    });

 // Determinar qué oferta le corresponde
  const getOfferType = (score) => {
    if (score >= 0 && score <= 6) return 'ofertaA';
    if (score >= 7 && score <= 8) return 'ofertaB';
    if (score === 9) return 'ofertaC';
    if (score === 10) return 'ofertaD';
    return null;
  };

  const offerType = getOfferType(score);
  const percentage = ((score / 10) * 100).toFixed(0);
  
  // Datos completos para el webhook
  const webhookData = {
    // Información del usuario
    userId: userId,
    userName: userName,
    userEmail: user.email || 'No disponible',
    
    // Resultados del examen
    score: score,
    totalQuestions: 10,
    percentage: percentage,
    
    // Respuestas detalladas
    answers: answersWithCorrectness,
    correctAnswers: score,
    incorrectAnswers: 10 - score,
    
    // Oferta asignada
    offerType: offerType,
    offerCategory: getOfferCategory(score),
      webhookId: `${userId}_${Date.now()}`,
    // Timestamps
    fechaCompletado: new Date().toISOString(),
    timestamp: new Date().toISOString(),
    fechaCompletadoLocal: new Date().toLocaleString('es-ES', {
      timeZone: 'America/Santiago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }),
    
    // Metadata adicional
    examDuration: null, // Puedes calcular esto si trackeas el tiempo de inicio
    browser: navigator.userAgent,
    platform: navigator.platform
  };



    const userStatusDocRef = doc(db, `artifacts/${appId}/users/${userId}/user_data/exam_status`);
  const userPrivateDocRef = doc(db, `artifacts/${appId}/users/${userId}`); // ✅ Datos privados
  const userPublicDocRef = doc(db, `artifacts/${appId}/public/data/exam_results/${userId}`); // ✅ Datos públicos

  try {
    webhookSentRef.current = true;
    // 1. ✅ ACTUALIZAR registro privado del usuario
    const existingUserData = await getExistingUserData(userId, db, 'private');
    
    await setDoc(userPrivateDocRef, {
      // Mantener datos de registro originales
      ...existingUserData,
      
      // Agregar/actualizar datos del examen
      answers: answersWithCorrectness,
      score: score,
       offerType: offerType,
    offerCategory: getOfferCategory(score),
      timestamp: new Date().toISOString(),
      fechaCompletado: new Date(),
      fechaCompletadoLocal: new Date().toLocaleString('es-ES', {
        timeZone: 'America/Santiago'
      }),
      
      // Actualizar estado
      status: 'exam_completed',
      examCompleted: true,
      
      // Metadata del examen
      examBrowser: navigator.userAgent,
      examPlatform: navigator.platform
    }, { merge: true });

    // 2. ✅ CREAR/ACTUALIZAR registro público (para admin)
    await setDoc(userPublicDocRef, {
      userId: userId,
      userName: userName,
      email: user.email,
      answers: answersWithCorrectness,
      score: score,
       offerType: offerType,
    offerCategory: getOfferCategory(score),
      timestamp: new Date().toISOString(),
      fechaCompletado: new Date(),
      
      // Datos adicionales para admin
      fechaRegistro: existingUserData.fechaRegistro || new Date().toISOString(),
      pais: existingUserData.pais || null
    });

    // 3. ✅ ACTUALIZAR intentos del usuario
    await setDoc(userStatusDocRef, {
      attempts: 1,
    }, { merge: true });


const zapierWebhookUrl = "https://hooks.zapier.com/hooks/catch/8758083/uhl8lki/";
    
    try {
      const webhookResponse = await fetch(zapierWebhookUrl, {
       method: 'POST',
      
        body: JSON.stringify(webhookData)
      });

      if (webhookResponse.ok) {
        console.log('✅ Webhook enviado exitosamente a Zapier');
      } else {
        console.error('❌ Error al enviar webhook:', webhookResponse.status, webhookResponse.statusText);
      }
    } catch (webhookError) {
      console.error('❌ Error de red al enviar webhook:', webhookError);
      // No fallar el proceso si el webhook falla
      webhookSentRef.current = false;
    }

        setView('userResults');
    } catch (error) {
        console.error("Error saving exam results:", error);
        webhookSentRef.current = false;
    }
  };

const getOfferCategory = (score) => {
  if (score >= 0 && score <= 6) return 'Principiante';
  if (score >= 7 && score <= 8) return 'Intermedio';
  if (score === 9) return 'Avanzado';
  if (score === 10) return 'Experto';
  return 'Sin categoría';
};

const getExistingUserData = async (userId, db, type = 'private') => {
  try {
    const appId = 'examenes';
    let userDocRef;
    
    if (type === 'private') {
      userDocRef = doc(db, `artifacts/${appId}/users/${userId}`);
    } else {
      userDocRef = doc(db, `artifacts/${appId}/public/data/exam_results/${userId}`);
    }
    
    const docSnap = await getDoc(userDocRef);
    return docSnap.exists() ? docSnap.data() : {};
  } catch (error) {
    console.error('Error obteniendo datos existentes:', error);
    return {};
  }
};

  const handleLogin = useCallback(async (email, password) => {
    const auth = getAuth();
    await signInWithEmailAndPassword(auth, email, password);
  }, []);


const getUserLocation = async () => {
  try {
    // Usar fetch en lugar de jQuery para mantener consistencia
    const response = await fetch('https://api.ipstack.com/check?access_key=0a95b34315b92b69573705a1b26f4930');
    
    if (!response.ok) {
      throw new Error('Error en la respuesta de ipstack');
    }
    
    const locacion = await response.json();
    
    return {
      continentCode: locacion.continent_code || null,
      countryCode: locacion.country_code || null,
      countryName: locacion.country_name || null,
      city: locacion.city || null,
      region: locacion.region_name || null,
      ip: locacion.ip || null,
      // Datos adicionales que podrían ser útiles
      latitude: locacion.latitude || null,
      longitude: locacion.longitude || null,
      timezone: locacion.time_zone?.id || null
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo ubicación:', error);
    // Retornar valores null si falla la API
    return {
      continentCode: "NA",
      countryCode: null,
      countryName: null,
      city: null,
      region: null,
      ip: null,
      latitude: null,
      longitude: null,
      timezone: null
    };
  }
};

const createInitialUserRecord = async (user, db) => {
  try {
 console.log('🌍 Obteniendo ubicación del usuario...');
    
    // ✅ Obtener ubicación del usuario
    const locationData = await getUserLocation();
    
    console.log('📍 Ubicación obtenida:', locationData);

    const appId = 'examenes';
    const userDocRef = doc(db, `artifacts/${appId}/users/${user.uid}`);
    
    // Datos iniciales del usuario
    const initialUserData = {
      // Información básica del usuario
      userId: user.uid,
      email: user.email,
      userName: user.displayName || 'Usuario Sin Nombre',
      
      // Timestamps de registro
      fechaRegistro: new Date().toISOString(),
      fechaRegistroLocal: new Date().toLocaleString('es-ES', {
        timeZone: 'America/Santiago',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      
      // Campos relacionados al examen (inicialmente null/vacío)
      score: null,
      answers: null,
      timestamp: null,
      fechaCompletado: null,
      
      // ✅ UBICACIÓN DEL USUARIO (nuevos campos)
      continentCode: locationData.continentCode,
      countryCode: locationData.countryCode,
      pais: locationData.countryName, // Nombre del país completo
      ciudad: locationData.city,
      region: locationData.region,
      ip: locationData.ip,
      coordenadas: {
        latitude: locationData.latitude,
        longitude: locationData.longitude
      },
      timezone: locationData.timezone,
      
      // Estado del registro
      status: 'registered', // 'registered' -> 'exam_completed'
      examCompleted: false,
      
      // Metadata
      registrationSource: 'web_app',
      browser: navigator.userAgent,
      platform: navigator.platform
    };

    // Crear el documento inicial
    await setDoc(userDocRef, initialUserData);
    
    console.log('✅ Registro inicial creado para:', user.email);
    return true;
    
  } catch (error) {
    console.error('❌ Error creando registro inicial:', error);
    return false;
  }
};



 const handleRegister = useCallback(async (name, email, password) => {
  try {
    const auth = getAuth();
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Actualizar el perfil con el nombre
    await updateProfile(userCredential.user, { displayName: name });
    
    // ✅ NUEVO: Crear registro inicial en exam_results
    if (db) {
      await createInitialUserRecord(userCredential.user, db);
    }
    window.history.pushState({}, '', '/exito');
    
    console.log('✅ Usuario registrado y URL actualizada a /exito');
    
    console.log('✅ Usuario registrado y registro inicial creado');
    
  } catch (error) {
    console.error('❌ Error en registro:', error);
    throw error;
  }
}, [db]);

  const handleSignOut = async () => {
    const auth = getAuth();
    await signOut(auth);
    setUser(null);
    setView('login');
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  if (!isAuthReady) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl text-gray-600">Cargando aplicación...</div>
      </div>
    );
  }

  // Renderizado condicional de las vistas
  const renderView = () => {
    if (!user) {
      return <AuthScreen onLogin={handleLogin} onRegister={handleRegister} />;
    }
    
    if (user.email === 'soporte@astroterapeutica.com') {
      return <ResultsPage db={db} onSignOut={handleSignOut} />;
    }

    if (view === 'instructions') {
      return <InstructionsPage onStartExam={startExam} />;
    }
    
    if (view === 'exam') {
      const currentQuestion = examState.questions[examState.currentQuestionIndex]?.question;
      const isStructuredQuestion = typeof currentQuestion === 'object' && currentQuestion !== null;

      return (
        <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4 pt-28">
          <style>
            {`
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
            body { font-family: 'Inter', sans-serif; }
            .fade-in { animation: fadeIn 0.5s ease-in-out; }
            @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}
          </style>
          <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-2xl">
            {examState.isExamActive ? (
              <div className="fade-in">
                <h3 className="text-lg font-semibold text-gray-600 mb-2">
                  Pregunta {examState.currentQuestionIndex + 1} de {examState.questions.length}
                </h3>
                <div className="flex justify-center items-center mb-6">
                  <p className="text-4xl font-extrabold text-blue-600 animate-pulse">{formatTime(examState.timer)}</p>
                </div>
                <div className="text-xl md:text-2xl mb-8 text-gray-800 transition-opacity duration-500 select-none">
                  {/*
                    Using dangerouslySetInnerHTML to render questions with formatting.
                    This is generally not recommended for user-provided content due to XSS risks.
                    However, since the questions are defined statically in the code, it is safe to use here.
                  */}
                 {isStructuredQuestion
    ? renderTextWithFormatting(currentQuestion.text)
    : renderTextWithFormatting(currentQuestion)}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {examState.questions[examState.currentQuestionIndex]?.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(option)}
                      className={`py-4 px-6 text-left font-semibold rounded-lg shadow-md transition-all transform hover:scale-105 ${
                        selectedAnswer === option
                          ? 'bg-blue-600 text-white border-2 border-blue-800'
                          : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                <button
                  onClick={handleNextQuestion}
                  disabled={!selectedAnswer}
                  className={`mt-6 w-full py-3 px-6 font-bold rounded-lg shadow-md transition-all transform ${
                    selectedAnswer
                      ? 'bg-green-600 text-white hover:bg-green-700 hover:scale-105'
                      : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                  }`}
                >
                  {examState.currentQuestionIndex === examState.questions.length - 1 ? 'Finalizar Examen' : 'Siguiente'}
                </button>
              </div>
            ) : (
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">¡Examen listo!</h2>
                
              </div>
            )}
          </div>
        </div>
      );
    }

    if (view === "sessionExpired") {
  return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4 pt-28">
          <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md text-center">
            <div className="text-red-600 text-6xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold text-red-600 mb-4">Sesión Caducada</h2>
            <p className="text-lg text-gray-700 mb-6">
              Has infringido las reglas para realizar el examen.
            </p>
           
          </div>
        </div>
      );
    }

    if (view === 'userResults') {
      return <UserResultsPage onSignOut={handleSignOut} user={user} db={db} />;
    }
    
    if (view === 'message') {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4 pt-28">
          <style>
            {`
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
            body { font-family: 'Inter', sans-serif; }
            `}
          </style>
          <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md text-center">
            <h2 className="text-2xl font-bold text-green-600 mb-4">¡Participación recibida!</h2>
            <p className="text-lg text-gray-700">Gracias por completar el examen. Tus resultados han sido registrados.</p>
            <div className="mt-6 flex justify-center space-x-4">
              <button onClick={handleSignOut} className="py-2 px-4 bg-red-600 text-white rounded-lg shadow-md hover:bg-red-700 transition-colors">Cerrar Sesión</button>
            </div>
          </div>
        </div>
      );
    }


    
  };

  return (
    <>
      <Header />
       {showWarningModal && <WarningModal onAccept={handleWarningAccept} />}
      {renderView()}
      
    </>
  );
};


export default QuizApp;
